import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../context/LanguageContext';
import {
  RefreshCw, AlertTriangle,
  CheckCircle, Cpu, Database, Layers, HelpCircle
} from 'lucide-react';
import UniversalList from '../../components/UniversalList';

const apiFetch = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(url, { ...options, headers });
  if (res.status === 401) { localStorage.removeItem('token'); window.location.href = '/login'; throw new Error('Session expired'); }
  return res.json();
};

const TabButton = ({ active, label, icon, onClick }) => (
  <button onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
      active
        ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border border-blue-200 dark:border-blue-800'
        : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50'
    }`}>
    {icon}{label}
  </button>
);

const AttributeMappingPage = () => {
  const { t } = useTranslation();
  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [activeTab, setActiveTab] = useState('mapping');
  const [syncing, setSyncing] = useState(false);
  const [syncStatusLoading, setSyncStatusLoading] = useState(false);
  const [error, setError] = useState('');
  const [storeSearch, setStoreSearch] = useState('');
  const [setSearch, setSetSearch] = useState('');

  // Data states
  const [syncData, setSyncData] = useState(null);
  const [storeSets, setStoreSets] = useState([]);
  const [store, setStore] = useState(null);
  const [lastSyncResult, setLastSyncResult] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  // ── Load stores ──
  useEffect(() => {
    apiFetch('/api/channel/stores').then(res => {
      if (res.status === 'success') {
        const s = res.data.stores || [];
        setStores(s);
        if (s.length === 1) setSelectedStoreId(String(s[0].auth_id));
      }
    }).catch(() => {});
  }, []);

  // ── Load store data when selected ──
  useEffect(() => {
    if (!selectedStoreId) return;
    setError('');

    apiFetch(`/api/channel/stores/${selectedStoreId}`)
      .then(res => { if (res.status === 'success') setStore(res.data); })
      .catch(() => {});

    apiFetch(`/api/channel/attributes/store-sets?store_id=${selectedStoreId}`)
      .then(res => {
        if (res.status === 'success') setStoreSets(res.data.sets || []);
      })
      .catch(() => {});

    loadSyncStatus(selectedStoreId);
  }, [selectedStoreId]);

  const loadSyncStatus = async (storeId) => {
    if (!storeId) return;
    setSyncStatusLoading(true);
    try {
      const res = await apiFetch(`/api/channel/attributes/sync-status?store_id=${storeId}`);
      if (res.status === 'success') setSyncData(res.data);
      else setSyncData(null);
    } catch {
      setSyncData(null);
    } finally {
      setSyncStatusLoading(false);
    }
  };

  const handleSync = async () => {
    if (!selectedStoreId) return;
    setSyncing(true);
    setError('');
    try {
      const res = await apiFetch('/api/channel/attributes/sync', {
        method: 'POST',
        body: JSON.stringify({ store_id: parseInt(selectedStoreId) }),
      });
      if (res.status === 'success') {
        setLastSyncResult(res.data);
        await loadSyncStatus(selectedStoreId);
      } else {
        setError(res.message || 'Sync failed');
      }
    } catch (err) {
      setError('Sync error: ' + err.message);
    } finally {
      setSyncing(false);
    }
  };

  const selectedStore = stores.find(s => String(s.auth_id) === selectedStoreId);
  const filteredAttrs = (syncData?.attributes || []).filter(a => {
    if (!storeSearch) return true;
    const q = storeSearch.toLowerCase();
    return (a.attribute_code || '').toLowerCase().includes(q)
      || (a.attribute_label || '').toLowerCase().includes(q)
      || (a.input_type || '').toLowerCase().includes(q)
      || (a.erp_attribute_code || '').toLowerCase().includes(q);
  });

  // Reset to page 1 when search or store changes
  useEffect(() => { setPage(1); }, [storeSearch, selectedStoreId]);

  // Reset to page 1 when sets tab search changes
  useEffect(() => { setPage(1); }, [setSearch, activeTab]);

  const totalFiltered = filteredAttrs.length;
  const paginatedAttrs = filteredAttrs.slice((page - 1) * limit, page * limit);

  // Sets tab search + pagination
  const filteredSets = setSearch
    ? storeSets.filter(s => {
        const q = setSearch.toLowerCase();
        return (s.set_name || '').toLowerCase().includes(q)
          || (s.set_code || '').toLowerCase().includes(q)
          || (s.entity_type || '').toLowerCase().includes(q);
      })
    : storeSets;
  const totalFilteredSets = filteredSets.length;
  const paginatedSets = filteredSets.slice((page - 1) * limit, page * limit);

  const attrColumns = [
    {
      key: 'attribute_code',
      label: t('channel.attr_code'),
      className: 'font-mono text-sm',
    },
    {
      key: 'attribute_label',
      label: t('channel.attr_label'),
      render: (val, row) => (
        <span className="inline-flex items-center gap-2">
          <span>{val || row.attribute_code}</span>
          {row.is_system_component && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-800">
              <Cpu size={10} />
              {t('channel.system_component')}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'input_type',
      label: t('channel.input_type'),
      render: (val) => (
        <span className="inline-block px-2 py-0.5 bg-slate-100 dark:bg-slate-700 rounded text-xs text-slate-600 dark:text-slate-400">
          {val || '-'}
        </span>
      ),
    },
    {
      key: 'status',
      label: t('channel.status'),
      render: (_val, row) => {
        if (row.is_mapped == 1) {
          return (
            <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800">
              <CheckCircle size={12} />{t('channel.mapped')}
            </span>
          );
        }
        return (
          <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800">
            <HelpCircle size={12} />{t('channel.pending')}
          </span>
        );
      },
    },
    {
      key: 'erp_mapping',
      label: t('channel.erp_mapping'),
      render: (_val, row) => {
        if (row.is_system_component) {
          return (
            <span className="inline-flex items-center gap-1">
              <Cpu size={12} className="text-purple-500 shrink-0" />
              <span className="font-medium text-xs text-purple-600 dark:text-purple-400">{t('channel.system_component')}</span>
            </span>
          );
        }
        if (row.erp_attribute_id != null) {
          return (
            <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
              <CheckCircle size={12} className="shrink-0" />
              <span className="font-mono text-xs truncate">{row.erp_attribute_code || t('channel.auto_created_short')}</span>
            </span>
          );
        }
        return <span className="text-slate-400 italic text-xs">{t('channel.not_mapped')}</span>;
      },
    },
  ];

  const setColumns = [
    {
      key: 'set_name',
      label: t('channel.set_name'),
      className: 'text-sm font-medium',
    },
    {
      key: 'set_code',
      label: t('channel.set_code'),
      className: 'font-mono text-sm',
    },
    {
      key: 'entity_type',
      label: t('channel.entity_type'),
      render: (val) => (
        <span className="inline-block px-2 py-1 text-xs rounded bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400">
          {val}
        </span>
      ),
    },
    {
      key: 'auth_id',
      label: t('channel.auth_id'),
      render: (val) => (
        <span className="inline-block px-2 py-1 text-xs rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400">
          {val || t('channel.system')}
        </span>
      ),
    },
  ];

  if (!selectedStoreId) {
    return (
      <div className="space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-bold">{t('channel.attribute_mapping')}</h1>
          <p className="text-slate-500 text-sm mt-1">{t('channel.attribute_mapping_desc')}</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Database size={18} className="text-slate-400" />
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('channel.store')}:</label>
            <select value={selectedStoreId}
              onChange={e => { setSelectedStoreId(e.target.value); }}
              className="rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm dark:text-slate-200 focus:ring-2 focus:ring-blue-500 min-w-[200px]">
              <option value="">-- {t('common.select')} --</option>
              {stores.map(s => (
                <option key={s.auth_id} value={s.auth_id}>{s.store_name || s.auth_id} ({s.store_url || ''})</option>
              ))}
            </select>
          </div>
          <p className="text-sm text-slate-400 italic">{t('channel.select_store_prompt')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Title + Store bar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">{t('channel.attribute_mapping')}</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{t('channel.attribute_mapping_desc')}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-slate-600 dark:text-slate-400 whitespace-nowrap">
              <Database size={14} className="inline mr-1" />
              {t('channel.store')}:
            </label>
            <select value={selectedStoreId}
              onChange={e => { setSelectedStoreId(e.target.value); setLastSyncResult(null); }}
              className="rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm dark:text-slate-200 focus:ring-2 focus:ring-blue-500 min-w-[180px]">
              <option value="">-- {t('common.select')} --</option>
              {stores.map(s => (
                <option key={s.auth_id} value={s.auth_id}>{s.store_name || s.auth_id}</option>
              ))}
            </select>
          </div>
          <button onClick={handleSync} disabled={syncing}
            className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50">
            <Database size={16} className={syncing ? 'animate-pulse' : ''} />
            {syncing ? t('channel.syncing') : t('channel.sync_from_m2')}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded-lg px-4 py-3 border border-amber-200 dark:border-amber-800">
          <AlertTriangle size={16} />
          {error}
        </div>
      )}

      {/* Sync result summary */}
      {lastSyncResult && (
        <div className={`flex items-center gap-3 text-sm rounded-lg px-4 py-3 border ${
          lastSyncResult.success
            ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800'
        }`}>
          {lastSyncResult.success ? <CheckCircle size={18} /> : <AlertTriangle size={18} />}
          <div>
            <p className="font-medium">{t('channel.sync_complete')}</p>
            <p className="text-xs mt-0.5 opacity-80">
              {t('channel.synced_attrs', { count: lastSyncResult.synced })} · {t('channel.created_attrs', { count: lastSyncResult.created })} · {t('channel.mapped_attrs', { count: lastSyncResult.mapped })}
              {lastSyncResult.sets?.length > 0 && ` · ${lastSyncResult.sets.length} sets`}
            </p>
            {lastSyncResult.warnings?.length > 0 && (
              <div className="mt-1 text-xs opacity-70">
                {lastSyncResult.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        <TabButton active={activeTab === 'mapping'}
          label={t('channel.attr_sync_status')}
          icon={<RefreshCw size={16} />}
          onClick={() => setActiveTab('mapping')} />
        <TabButton active={activeTab === 'sets'}
          label={t('channel.attribute_sets')}
          icon={<Layers size={16} />}
          onClick={() => setActiveTab('sets')} />
      </div>

      {/* ── Tab 1: Sync Status / Auto-mapping ── */}
      {activeTab === 'mapping' && (
        <>
          {/* Summary cards */}
          {syncData && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-white dark:bg-slate-800 rounded-xl px-4 py-3 border border-slate-200 dark:border-slate-700 shadow-sm">
                <p className="text-xs text-slate-500 dark:text-slate-400">{t('channel.total_synced')}</p>
                <p className="text-2xl font-bold text-slate-800 dark:text-slate-100 mt-1">{syncData.summary?.total || 0}</p>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-xl px-4 py-3 border border-green-200 dark:border-green-800 shadow-sm">
                <p className="text-xs text-slate-500 dark:text-slate-400">{t('channel.auto_mapped')}</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">{syncData.summary?.mapped || 0}</p>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-xl px-4 py-3 border border-blue-200 dark:border-blue-800 shadow-sm">
                <p className="text-xs text-slate-500 dark:text-slate-400">{t('channel.auto_created')}</p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">{syncData.summary?.created || 0}</p>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-xl px-4 py-3 border border-slate-200 dark:border-slate-700 shadow-sm">
                <p className="text-xs text-slate-500 dark:text-slate-400">{t('channel.attr_sets')}</p>
                <p className="text-2xl font-bold text-slate-800 dark:text-slate-100 mt-1">{syncData.summary?.sets || 0}</p>
              </div>
            </div>
          )}

          <UniversalList
            columns={attrColumns}
            data={paginatedAttrs}
            loading={syncStatusLoading}
            emptyMessage={syncStatusLoading ? t('channel.loading_sync_data') : t('channel.no_attributes_synced')}
            loadingText={t('common.loading')}
            rowKey={(row) => row.attribute_code || row.id || Math.random()}
            page={page}
            totalItems={totalFiltered}
            pageSize={limit}
            setPage={setPage}
            setPageSize={setLimit}
            searchPlaceholder={t('channel.search_attributes')}
            searchTerm={storeSearch}
            onSearchChange={setStoreSearch}
          />
        </>
      )}

      {/* ── Tab 2: Store-specific Attribute Sets ── */}
      {activeTab === 'sets' && (
        <UniversalList
          columns={setColumns}
          data={paginatedSets}
          loading={false}
          emptyMessage={t('channel.no_attr_sets')}
          rowKey={(row) => row.set_code || row.id || Math.random()}
          page={page}
          totalItems={totalFilteredSets}
          pageSize={limit}
          setPage={setPage}
          setPageSize={setLimit}
          searchPlaceholder={t('common.search')}
          searchTerm={setSearch}
          onSearchChange={setSetSearch}
        />
      )}
    </div>
  );
};

export default AttributeMappingPage;
